import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {FolderList} from "./folder";
import Drawer from "@material-ui/core/Drawer";
import React, {useEffect, useState, memo} from "react";
import {createStyles, makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import { AppBar, Box, Tab } from "@material-ui/core";
import {
  TabPanel,
  TabProps,
} from "./components";
import {
  changeDrawerOpen,
  changeSelectedTab,
  selectSelectedTab,
} from "./redux/settings-slice";
import Tabs from "@material-ui/core/Tabs/Tabs";
import { useTranslation } from 'react-i18next';
import { BookmarkList } from "./bookmarks";
import { useDispatch, useSelector } from 'react-redux';
import { Bookmark } from "./redux/bookmarks-slice";
import { useBookmarks, useViewer } from "./hooks";

export const assetDrawerWidth = 280;
export const assetDrawerHeight = 75;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      display: "none"
    },
    inside: {
      height: `${assetDrawerHeight}%`,
      display: "flex",
      overflow: "hidden",
      flexDirection: "column"
    },
    drawer: {
      width: 0,
    },
    drawerPaper: {
      width: assetDrawerWidth,
    },
    root: {
      "z-index": `${theme.zIndex.modal - 1} !important`
    },
    tab: {
      minWidth: "0px !important",
      flexBasis: "unset",
      flexShrink: "unset"
    },
    tabGrow: {
      "flex-grow": 1
    },
    toggleDrawer: {
      color: "white"
    }
  }),
);

interface StyledPanelProps {
  value: number;
  index: number;
  children: React.ReactNode;
}

const StyledPanel = (props: StyledPanelProps) => {
  return (
    <TabPanel
      PanelProps={{
        style: {
          flex: 1,
          overflow: "hidden"
        }
      }}
      BoxProps={{
        style: {
          padding: "10px 0px 0px 0px",
          height: "calc(100% - 10px)",
          overflow: "auto",
          display: "flex",
          flexDirection: "column"
        }
      }}
      value={props.value}
      index={props.index}
    >
      {props.children}
    </TabPanel>
  );
};

interface AssetDrawerProps {
  assetDrawerState: boolean;
}

export const AssetDrawer = memo((props: AssetDrawerProps) => {
  const {assetDrawerState} = props;

  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const {bookmarks, loadBookmark} = useBookmarks();

  const projectActiveTab = useSelector(selectSelectedTab);

  const [activeTab, setSelectedTab] = useState(0);

  const setActiveTab = (event, value) => {
    dispatch(changeSelectedTab(value));
  };

  const handleDrawerClose = () => {
    dispatch(changeDrawerOpen(false));
  };

  useEffect(() => {
    setSelectedTab(projectActiveTab);
  }, [projectActiveTab]);

  useEffect(() => {
    if (!viewer) return;

    const newAnnotations = [];

    bookmarks.forEach((bookmark: Bookmark) => {
      if (!bookmark.visible) return;

      const onClick = () => {
        loadBookmark(bookmark.id);
      };

      const {name, position} = bookmark;
      const annotation = viewer.addAnnotation(name, position, onClick);
      newAnnotations.push(annotation);
    });

    viewer.updateAnnotations();

    return () => {
      newAnnotations.forEach(annotation => {
        viewer.removeAnnotation(annotation);
      });

      viewer.updateAnnotations();
    };
  }, [viewer, bookmarks]);

  return (
    <Drawer
      className={classes.drawer}
      anchor="left"
      open={assetDrawerState}
      transitionDuration={0}
      classes={{
        paper: classes.drawerPaper,
        root: classes.root
      }}
      BackdropProps={{
        classes: {
          root: classes.backdrop
        }
      }}
    >
      <div className={classes.inside}>
        <AppBar position="static" color="primary">
          <Box display="flex" flexDirection="row">
            <Tabs
              value={activeTab}
              onChange={setActiveTab}
              variant="fullWidth"
              className={classes.tabGrow}
            >
              <Tab label={t("app-bar.tab-manage")} className={classes.tab} {...TabProps(0)} />
              <Tab label={t('app-bar.tab-bookmarks')} className={classes.tab} {...TabProps(1)} />
            </Tabs>
            <IconButton onClick={handleDrawerClose} className={classes.toggleDrawer}>
              {theme.direction === 'ltr' ? <ChevronLeftIcon/> : <ChevronRightIcon/>}
            </IconButton>
          </Box>
        </AppBar>

        {/* Assets */}
        <StyledPanel value={activeTab} index={0}>
          <FolderList/>
        </StyledPanel>

        {/* Bookmarks */}
        <StyledPanel value={activeTab} index={1}>
          <BookmarkList/>
        </StyledPanel>
      </div>
    </Drawer>
  );
});
